/* eslint-disable no-nested-ternary */
import React, { useCallback, useContext, useMemo, useState } from "react"
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import MaterialReactTable from "material-react-table"
import {
    AttachMoney,
    Edit,
    HighlightOff,
    PeopleOutlined,
    TaskAlt,
    Visibility,
} from "@mui/icons-material"
import { DateTime } from "luxon"
import { useSnackbar } from "notistack"
import { useMutation } from "react-query"
import { Link, useNavigate, useParams } from "react-router-dom"

import useTableState from "../../Hooks/useTableState.js"
import { CompanyInfo } from "../SystemUsers/companyList.jsx"
import useTableProps from "../../Hooks/useTableProps.js"
import useUserPermissions from "../../Hooks/useUserPermissions.js"
import useFetchEntitiesPerPage from "../../Hooks/useFetchEntitiesPerPage.js"
import usePartialUpdateEntity from "../../Hooks/usePartialUpdateEntity.js"
import useAxiosPrivate from "../../Hooks/useAxiosPrivate.js"

import StatusButton from "../../Component/statusButton.jsx"
import ViewTripDetails from "../../Component/viewTripDetail.jsx"
import customeFilter from "../../Component/customFilterPanel.jsx"
import RestrictedAccess from "../../Component/restrictedAccess.jsx"
import CustomeDateFilter from "../../Component/customDateFilterPanel.jsx"

import AddMembers from "./addMembers.jsx"
import AuthContext from "../../Context/auth_provider.jsx"
import PaymentTypeSelector from "./paymentTypeSelector.jsx"
import EmergencyTripFormDialog from "./emergencyTripForm.jsx"
import { formatToTwoDecimalPlaces } from "../../Utils/dataFormat.js"
import UpdatePreRIDEPrograms from "../../Component/updatePreRIDEPrograms.jsx"

const emergencyTrips = [
    {
        accessorKey: "created_at",
        header: "Date",
        size: 200,
        filterVariant: "date",
        renderColumnFilterModeMenuItems: customeFilter,
        Cell: ({ cell }) => DateTime.fromISO(cell.getValue()).toFormat("ff"),
        Filter: (props) => <CustomeDateFilter {...props} />,
    },
    {
        accessorKey: "phone",
        header: "User Phone",
        renderColumnFilterModeMenuItems: customeFilter,
        size: 200,
    },
    {
        accessorKey: "first_name",
        header: "User Full Name",
        Cell: ({ row }) =>
            `${row.original.user?.first_name ?? ""} ${
                row.original.user?.middle_name ?? ""
            } ${row.original.user?.last_name ?? ""}`,
        renderColumnFilterModeMenuItems: customeFilter,
        size: 200,
    },
    {
        accessorKey: "start_location",
        header: "Start Location",
        renderColumnFilterModeMenuItems: customeFilter,
        size: 200,
    },
    {
        accessorKey: "end_location",
        header: "End Location",
        renderColumnFilterModeMenuItems: customeFilter,
        size: 200,
    },
    {
        accessorKey: "trip_type",
        header: "Trip Type",
        renderColumnFilterModeMenuItems: customeFilter,
        size: 200,
        Cell: ({ row }) =>
            (row.original?.trip_type || row.original?.trip_type !== "") && (
                <Button
                    disableElevation
                    variant="contained"
                    color="common"
                    sx={{ py: ".5em", px: "1em", borderRadius: "1em" }}
                >
                    {row.original.trip_type}
                </Button>
            ),
    },
    {
        accessorKey: "payment_type",
        header: "Payment Type",
        renderColumnFilterModeMenuItems: customeFilter,
        size: 200,
        filterVariant: "select",
        filterSelectOptions: ["AUTOMATIC", "MANUAL"],
    },
    {
        accessorKey: "payment_status",
        header: "Payment Status",
        renderColumnFilterModeMenuItems: customeFilter,
        size: 200,
        Cell: ({ row }) =>
            (row.original?.payment_status ||
                row.original?.payment_status !== "") && (
                <Button
                    disableElevation
                    variant="contained"
                    color="common"
                    sx={{ py: ".5em", px: "1em", borderRadius: "1em" }}
                >
                    {row.original?.payment_status}
                </Button>
            ),
    },
    {
        accessorKey: "note",
        header: "Note",
        enableSorting: false,
        enableColumnFilter: false,
        renderColumnFilterModeMenuItems: customeFilter,
        size: 200,
    },
    {
        accessorKey: "reason_failed",
        header: "Failure Reason",
        renderColumnFilterModeMenuItems: customeFilter,
        size: 200,
    },
    {
        accessorKey: "updated_at",
        header: "Updated At",
        size: 200,
        filterVariant: "date",
        renderColumnFilterModeMenuItems: customeFilter,
        Cell: ({ cell }) => DateTime.fromISO(cell.getValue()).toFormat("ff"),
        Filter: (props) => <CustomeDateFilter {...props} />,
    },
    {
        accessorKey: "status",
        header: "Status",
        size: 200,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: "select",
        filterSelectOptions: ["PENDING", "APPROVED", "REJECTED"],
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "action",
        header: "Actions",
        size: 280,
        enableSorting: false,
        enableColumnFilter: false,
    },
]

const userFields = [
    {
        accessorKey: "created_at",
        header: "Created At",
        size: 200,
        filterVariant: "date",
        renderColumnFilterModeMenuItems: customeFilter,
        Cell: ({ cell }) =>
            `${DateTime.fromISO(cell.getValue()).toFormat("ff")}`,
    },
    {
        accessorKey: "first_name",
        header: "Name",
        size: 200,
        renderColumnFilterModeMenuItems: customeFilter,
        Cell: ({ row }) =>
            `${row.original?.first_name} ${row.original?.middle_name} ${row.original?.last_name}`,
    },
    {
        accessorKey: "phone",
        header: "Phone No",
        size: 150,
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "monthly_spent_amount",
        header: "Monthly Amount Spent",
        size: 150,
        filterVariant: "number",
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "total_spent_amount",
        header: "Total Amount Spent",
        size: 150,
        filterVariant: "number",
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "monthly_trips_used",
        header: "Monthly Trips Used",
        size: 150,
        filterVariant: "number",
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "total_trips_used",
        header: "Total Trips Used",
        size: 150,
        filterVariant: "number",
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "prerides",
        header: "Pre-RIDE Spending",
        size: 300,
        filterVariant: "json",
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "status",
        header: " ",
        size: 230,
        filterVariant: "select",
        filterSelectOptions: ["ACTIVE", "INACTIVE", "PENDING"],
        renderColumnFilterModeMenuItems: customeFilter,
        valueOptions: ["ACTIVE", "INACTIVE", "PENDING"],
    },
]
const preRideBudget = [
    {
        accessorKey: "name",
        header: "Name",
        size: 240,
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "total_trips_taken",
        header: "Total Trips",
        filterVariant: "number",
        size: 200,
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "monthly_trips_taken",
        header: "Monthly Trips",
        filterVariant: "number",
        size: 200,
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "monthly_spent_amount",
        header: "Monthly Spent Amount",
        filterVariant: "number",
        size: 200,
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "total_spent_amount",
        header: "Total Spent Amount",
        filterVariant: "number",
        size: 200,
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "number_of_users",
        header: "Number of Users",
        filterVariant: "number",
        size: 200,
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "spending_limit_amount",
        header: "Spending Limit Amount",
        size: 240,
        headerAlign: "center",
        filterVariant: "number",
        align: "center",
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "remaining_balance",
        header: "Remaining Balance",
        size: 240,
        headerAlign: "center",
        filterVariant: "select",
        filterSelectOptions: ["weekly", "daily", "monthly"],
        align: "center",
        renderColumnFilterModeMenuItems: customeFilter,
        Cell: ({ row }) =>
            formatToTwoDecimalPlaces(
                Number(row.original?.spending_limit_amount ?? 0) -
                    Number(row.original?.total_spent_amount ?? 0)
            ),
    },
    {
        accessorKey: "spending_limit_duration",
        header: "Spending Limit Duration",
        size: 240,
        headerAlign: "center",
        filterVariant: "select",
        filterSelectOptions: ["weekly", "daily", "monthly"],
        align: "center",
        renderColumnFilterModeMenuItems: customeFilter,
    },
    {
        accessorKey: "trip_limit_amount",
        header: "Trip Limit Amount",
        size: 240,
        filterVariant: "number",
        renderColumnFilterModeMenuItems: customeFilter,
        Cell: ({ cell }) => {
            const isUnlimited = cell.getValue() === 0
            return isUnlimited ? (
                <Typography>Unlimited</Typography>
            ) : (
                cell.getValue()
            )
        },
    },
    {
        accessorKey: "expire_date",
        header: "Expire Date",
        filterVariant: "date",
        size: 240,
        muiTableHeadCellFilterTextFieldProps: ({ column }) => ({
            type: "date",
            onChange: (event) => {
                column?.setFilterValue(event?.target?.value)
            },
        }),
        renderColumnFilterModeMenuItems: customeFilter,
        Cell: ({ cell, row }) => {
            const noExpireDate = row.original?.has_expire_date === false
            if (noExpireDate) return <Typography>No Expire Date</Typography>
            const expired =
                DateTime.now().startOf("day") >
                DateTime.fromISO(cell.getValue()).startOf("day")
            return expired ? (
                <Button
                    size="small"
                    disableElevation
                    variant="contained"
                    sx={{
                        backgroundColor: "#ffd6d6",
                        color: "red",
                        "&: hover": {
                            backgroundColor: "#ffd6d6",
                            color: "red",
                        },
                    }}
                >
                    Expired
                </Button>
            ) : (
                DateTime.fromISO(cell.getValue()).toFormat("ff")
            )
        },
    },
    {
        accessorKey: "status",
        header: "Actions",
        size: 360,
        filterVariant: "select",
        filterSelectOptions: ["ACTIVE", "INACTIVE", "PENDING"],
        renderColumnFilterModeMenuItems: customeFilter,
    },
]
function EmergencyTrips() {
    const {
        addTripUsers,
        getPreRideUsers,
        viewPreRideUsers,
        getEmergencyTrips,
        emergencyTripsPage,
        createEmergencyTrip,
        updateEmergencyTrip,
        preRideSpendingList,
        getTeamPreRideUsers,
        programsTransactions,
        createPreRideSpending,
        updatePreRideSpending,
        getPreRideTransactions,
        teamPreRideSpendingList,
        updateAssignedTeamPreRides,
        updateTeamPreRideUserStatus,
        assignTeamUserPreRideSpending,
    } = useUserPermissions()
    const [detail, setDetail] = useState(false)
    const [open, setOpen] = useState(false)
    const [addUser, setAddUser] = useState(false)
    const [tabName, setTabName] = useState("program") // Pre-RIDE Programs list | Emergency Trips list | Emergency trip users list
    const [isEditOpen, setIsEditOpen] = useState(false)
    const [user, setUser] = useState(null)
    // State for approve/reject dialogs
    const [approveDialog, setApproveDialog] = useState({
        open: false,
        trip: null,
    })
    const [rejectDialog, setRejectDialog] = useState({
        open: false,
        trip: null,
    })

    const { tId } = useParams()
    const navigate = useNavigate()
    const { companyId, teamId } = useContext(AuthContext)
    const { selectedCompany } = useContext(CompanyInfo)
    const { preride } = selectedCompany?.preride_program_config || {}

    const teamEndPoint = tId ? `teams/${tId}/` : ""
    const teamUsersEndPoint = tId ? `team/${tId}/` : ""
    const domain = teamId ? `/team/${teamId}` : `/corporate/${companyId}`

    const axiosPrivate = useAxiosPrivate()
    const { enqueueSnackbar } = useSnackbar()
    const { mutate, isLoading: updating } = usePartialUpdateEntity(
        `${teamEndPoint}preride`
    )
    const { mutate: updateStatus, isLoading: statusUpdating } =
        usePartialUpdateEntity(`${teamEndPoint}prerides/users`)
    const { params, state, onChangeHandlers, density } = useTableState({
        columns:
            tabName === "trips"
                ? emergencyTrips
                : tabName === "users"
                ? userFields
                : preRideBudget,
    })

    const handleEmergencyTripView = useMemo(
        () => (row) => {
            setDetail({ ...row })
        },
        []
    )
    const handleTabChange = (selectedTab) => setTabName(selectedTab)

    const handlePreRIDEExport = useCallback(
        (pData) =>
            pData?.map((el) => ({
                ...el,
                created_at: DateTime.fromISO(el.created_at).toFormat("ff"),
                updated_at: DateTime.fromISO(el.updated_at).toFormat("ff"),
                expire_date: DateTime.fromISO(el.expire_date).toFormat("ff"),
            })),
        []
    )
    const exportProcessor = useCallback(
        (data) =>
            tabName === "trips"
                ? data?.map((el) => ({
                      created_at: DateTime.fromISO(el.created_at).toFormat(
                          "ff"
                      ),
                      user_name: `${el.User?.first_name} ${el.User?.last_name} ${el.User?.last_name}`,
                      user_phone: el.phone ?? el.User?.phone,
                      start_location: el.start_location,
                      end_location: el.end_location,
                      note: el.note,
                      payment_type: el.payment_type,
                      status: el.status,
                      payment_status: el.payment_status,
                      reason_failed: el.reason_failed,
                      trip_type: el.reason_failed,
                      updated_at: DateTime.fromISO(el.updated_at).toFormat(
                          "ff"
                      ),
                  }))
                : data?.map((el) => ({
                      created_at: DateTime.fromISO(el.created_at).toFormat(
                          "ff"
                      ),
                      updated_at: DateTime.fromISO(el.updated_at).toFormat(
                          "ff"
                      ),
                      phone: el?.phone,
                      first_name: el?.first_name,
                      middle_name: el?.middle_name,
                      last_name: el?.last_name,
                  })),
        [tabName]
    )
    const exporter = useCallback(
        () => (tabName === "program" ? handlePreRIDEExport : exportProcessor),
        [handlePreRIDEExport, exportProcessor, tabName]
    )
    const {
        data: emergencyTripsData,
        isLoading,
        isFetching,
        refetch,
    } = useFetchEntitiesPerPage(
        {
            endPoint: `${teamEndPoint}emergency_trips`,
            ...params,
        },
        { enabled: Boolean(getEmergencyTrips && tabName === "trips") }
    )
    const {
        data: emergencyTripUsers,
        isFetching: fetchingUsers,
        refetch: refetchUsers,
    } = useFetchEntitiesPerPage(
        {
            endPoint: `${teamUsersEndPoint}prerides/users`,
            ...params,
        },
        {
            enabled: Boolean(
                (tId ? getTeamPreRideUsers : getPreRideUsers) &&
                    tabName === "users"
            ),
        }
    )
    const {
        data: preRIDE,
        isLoading: loadingPreRIDE,
        isFetching: fetchingPreRIDE,
        refetch: refetchPreRIDE,
    } = useFetchEntitiesPerPage(
        {
            endPoint: `${teamEndPoint}prerides`,
            ...params,
        },
        {
            enabled: tId ? teamPreRideSpendingList : preRideSpendingList,
        }
    )
    const { mutate: updateTripStatus, isLoading: updatingTripStatus } =
        useMutation(
            (data) =>
                axiosPrivate
                    .patch(`${domain}/emergency_trip/${data.id}/approve`, {
                        status: data.status,
                    })
                    .then((res) => res.data),
            {
                onError: (error) => {
                    enqueueSnackbar(
                        `Status update failed due to ${error.message}`,
                        { variant: "error" }
                    )
                    setApproveDialog({ open: false, trip: null })
                    setRejectDialog({ open: false, trip: null })
                },
                onSuccess: () => {
                    enqueueSnackbar("Status updated successfully", {
                        variant: "success",
                    })
                    refetch()
                    setApproveDialog({ open: false, trip: null })
                    setRejectDialog({ open: false, trip: null })
                },
            }
        )
    const handleStatusChange = useCallback(
        (checked, row) => {
            mutate(
                {
                    id: row?.id,
                    status: checked ? "ACTIVE" : "INACTIVE",
                },
                {
                    onError: (error) => {
                        enqueueSnackbar(
                            `status update failed due to ${error.message}`,
                            { variant: "error" }
                        )
                    },
                    onSuccess: () => {
                        enqueueSnackbar("status updated successfully", {
                            variant: "success",
                        })
                        refetchPreRIDE()
                    },
                }
            )
        },
        [enqueueSnackbar, mutate, refetchPreRIDE]
    )

    const handleUpdateStatus = useCallback(
        (checked, row) => {
            updateStatus(
                {
                    id: row?.id,
                    status: checked ? "ACTIVE" : "INACTIVE",
                },
                {
                    onError: (error) => {
                        enqueueSnackbar(
                            `status update failed due to ${error.message}`,
                            { variant: "error" }
                        )
                    },
                    onSuccess: () => {
                        enqueueSnackbar("status updated successfully", {
                            variant: "success",
                        })
                        refetchUsers()
                    },
                }
            )
        },
        [updateStatus, enqueueSnackbar, refetchUsers]
    )

    const paymentTypeColumn = useCallback(
        ({ cell, row }) => (
            <PaymentTypeSelector
                id={row.original.id}
                mode={cell.getValue()}
                refetch={refetch}
                disabled={
                    row.original.payment_status === "PENDING" &&
                    row.original?.trip_id !==
                        "00000000-0000-0000-0000-000000000000"
                }
            />
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )
    const preRIDEProgramActionColumn = useCallback(
        ({ cell, row }) => (
            <Box
                sx={{
                    display: "flex",
                    gap: "1.5em",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <StatusButton
                    loading={updating}
                    status={cell.getValue()}
                    onChange={(checked) =>
                        handleStatusChange(checked, row.original)
                    }
                />
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "space-around",
                    }}
                >
                    <Link to={`${row.original.id}/view`} state={row.original}>
                        <Tooltip title="see detail">
                            <IconButton
                                size="small"
                                sx={{ color: "primary.main" }}
                            >
                                <Visibility />
                            </IconButton>
                        </Tooltip>
                    </Link>
                    {!teamId && (
                        <Link
                            to={`${row.original.id}/edit`}
                            state={row.original}
                            style={{
                                pointerEvents: !updatePreRideSpending && "none",
                            }}
                        >
                            <Tooltip title="edit">
                                <IconButton
                                    disabled={!updatePreRideSpending}
                                    size="small"
                                    sx={{ color: "primary.main" }}
                                >
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                        </Link>
                    )}
                    <Link
                        to={`${row.original.id}/users`}
                        state={row.original}
                        style={{
                            pointerEvents:
                                (tId
                                    ? !getTeamPreRideUsers
                                    : !viewPreRideUsers) && "none",
                        }}
                    >
                        <Tooltip title="Pre-RIDE Spending Users">
                            <IconButton
                                disabled={
                                    tId
                                        ? !getTeamPreRideUsers
                                        : !viewPreRideUsers
                                }
                                size="small"
                                sx={{ color: "primary.main" }}
                            >
                                <PeopleOutlined />
                            </IconButton>
                        </Tooltip>
                    </Link>
                    <Link
                        to={`${row.original.id}/transactions`}
                        state={row.original}
                        style={{
                            pointerEvents: tId
                                ? !getPreRideTransactions
                                : !programsTransactions && "none",
                        }}
                    >
                        <Tooltip title="Transactions">
                            <IconButton
                                disabled={
                                    tId
                                        ? !getPreRideTransactions
                                        : !programsTransactions
                                }
                                size="small"
                                sx={{ color: "primary.main" }}
                            >
                                <AttachMoney />
                            </IconButton>
                        </Tooltip>
                    </Link>
                </Box>
            </Box>
        ),
        [
            tId,
            teamId,
            updating,
            viewPreRideUsers,
            getTeamPreRideUsers,
            programsTransactions,
            updatePreRideSpending,
            getPreRideTransactions,
            handleStatusChange,
        ]
    )
    const programEditColumn = useCallback(
        ({ cell, row }) => {
            const cellValue = cell.getValue()

            // Determine if prerides data is loading
            const isLoadingPrerides = (() => {
                if (!row?.original) return true

                // Check if user data is available
                const hasUserData =
                    row.original.phone || row.original.first_name
                const preRidesNotLoaded =
                    cellValue === undefined || cellValue === null

                return hasUserData && preRidesNotLoaded
            })()

            // Show loading state specifically for prerides data
            if (isLoadingPrerides) {
                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            minHeight: 40,
                            width: "100%",
                            px: 1,
                        }}
                    >
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            Loading programs...
                        </Typography>
                    </Box>
                )
            }

            const preRideData = Array.isArray(cellValue) ? cellValue : []

            return (
                <Autocomplete
                    key={`prerides-${row.original?.id}-${JSON.stringify(
                        cellValue
                    )}`} // Force re-render when data changes
                    readOnly
                    size={density === "compact" ? "small" : "medium"}
                    multiple
                    value={preRideData}
                    options={[]}
                    getOptionLabel={(opt) => opt?.name || ""}
                    renderTags={(value) => {
                        // Handle empty state
                        if (!Array.isArray(value) || value.length === 0) {
                            return (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        minHeight: 40,
                                        width: "100%",
                                        px: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "text.secondary",
                                            fontStyle: "italic",
                                        }}
                                    >
                                        No Pre-RIDE programs assigned
                                    </Typography>
                                </Box>
                            )
                        }

                        return value
                            .map((el, index) => {
                                if (!el || !el.name) return null

                                return (
                                    <Box
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${el.id || el.name}-${index}`}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            overflow: "hidden",
                                            m: ".05em",
                                            backgroundColor:
                                                el?.user_preride_status ===
                                                "ACTIVE"
                                                    ? "success.main"
                                                    : "error.main",
                                            color: "white",
                                            borderRadius: "20px",
                                            pr: "1em",
                                            minHeight: "28px",
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            sx={{
                                                color: "white",
                                                p: 0.5,
                                            }}
                                        >
                                            {el?.user_preride_status ===
                                            "ACTIVE" ? (
                                                <TaskAlt fontSize="small" />
                                            ) : (
                                                <HighlightOff fontSize="small" />
                                            )}
                                        </IconButton>
                                        <Typography
                                            variant="subtitle2"
                                            noWrap
                                            sx={{ maxWidth: 150 }}
                                            title={el.name}
                                        >
                                            {el.name}
                                        </Typography>
                                    </Box>
                                )
                            })
                            .filter(Boolean)
                    }}
                    renderInput={(inputParams) => (
                        <TextField
                            label="Pre-RIDE Spending"
                            {...inputParams}
                            InputProps={{
                                ...inputParams.InputProps,
                                sx: {
                                    minHeight: 40,
                                    "& .MuiInputBase-input": {
                                        display: "none",
                                    },
                                },
                            }}
                        />
                    )}
                    popupIcon={
                        updateAssignedTeamPreRides ? (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setIsEditOpen(true)
                                    setUser(row.original)
                                }}
                                sx={{
                                    color: "primary.main",
                                    "&:hover": {
                                        backgroundColor: "action.hover",
                                        color: "primary.dark",
                                    },
                                }}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        ) : null
                    }
                    sx={{
                        border: "none",
                        minHeight: 40,
                        width: "100%",
                        "&:hover": {
                            cursor: updateAssignedTeamPreRides
                                ? "pointer"
                                : "default",
                            backgroundColor: updateAssignedTeamPreRides
                                ? "action.hover"
                                : "transparent",
                        },
                        ".MuiAutocomplete-endAdornment": {
                            display: !updateAssignedTeamPreRides
                                ? "none"
                                : "flex",
                        },
                        ".MuiChip-filled": {
                            backgroundColor: "error.main",
                            color: "white",
                        },
                        ".MuiInputBase-root": {
                            minHeight: 40,
                            "& input": {
                                display: "none",
                            },
                        },
                        ".MuiAutocomplete-inputRoot": {
                            paddingRight: updateAssignedTeamPreRides
                                ? "39px !important"
                                : "9px !important",
                        },
                    }}
                />
            )
        },
        [density, updateAssignedTeamPreRides, setIsEditOpen, setUser]
    )
    const actionColumn = useCallback(
        ({ row }) => (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: "0.5em",
                }}
            >
                {updateEmergencyTrip &&
                    tabName === "trips" &&
                    row.original.status === "PENDING" && (
                        <>
                            <Button
                                size="small"
                                variant="contained"
                                sx={{
                                    backgroundColor: "#72cb85",
                                    color: "white",
                                    "&:hover": { backgroundColor: "#72cb85" },
                                    textTransform: "none",
                                    fontWeight: "bold",
                                    minWidth: "80px",
                                }}
                                onClick={() =>
                                    setApproveDialog({
                                        open: true,
                                        trip: row.original,
                                    })
                                }
                                disabled={updatingTripStatus}
                            >
                                {updatingTripStatus &&
                                approveDialog.trip?.id === row.original.id ? (
                                    <CircularProgress
                                        size={16}
                                        color="inherit"
                                    />
                                ) : (
                                    "Approve"
                                )}
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                sx={{
                                    backgroundColor: "#d73936",
                                    color: "white",
                                    "&:hover": { backgroundColor: "#d73936" },
                                    textTransform: "none",
                                    fontWeight: "bold",
                                    minWidth: "80px",
                                }}
                                onClick={() =>
                                    setRejectDialog({
                                        open: true,
                                        trip: row.original,
                                    })
                                }
                                disabled={updatingTripStatus}
                            >
                                {updatingTripStatus &&
                                rejectDialog.trip?.id === row.original.id ? (
                                    <CircularProgress
                                        size={16}
                                        color="inherit"
                                    />
                                ) : (
                                    "Reject"
                                )}
                            </Button>
                        </>
                    )}
                {row.original.trip_id &&
                    row.original.trip_id !==
                        "00000000-0000-0000-0000-000000000000" && (
                        <Tooltip title="View Trip Details">
                            <IconButton
                                onClick={() =>
                                    handleEmergencyTripView(row.original)
                                }
                                sx={{ color: "primary.main" }}
                            >
                                <Visibility />
                            </IconButton>
                        </Tooltip>
                    )}
            </Box>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            tabName,
            updateEmergencyTrip,
            updatingTripStatus,
            approveDialog,
            rejectDialog,
        ]
    )
    const statusColumn = useCallback(({ cell }) => {
        const status = cell.getValue()
        return (
            <Alert
                severity={
                    status === "PENDING"
                        ? "warning"
                        : status === "APPROVED"
                        ? "success"
                        : "error"
                }
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 160,
                    fontSize: "0.875rem",
                    fontWeight: "bold",
                }}
            >
                {status}
            </Alert>
        )
    }, [])
    const usersActionColumn = useCallback(
        ({ row }) => (
            <StatusButton
                loading={statusUpdating}
                disabled={tId ? !updateTeamPreRideUserStatus : false}
                status={row.original.status}
                onChange={(checked) =>
                    handleUpdateStatus(checked, row.original)
                }
            />
        ),
        [handleUpdateStatus, statusUpdating, tId, updateTeamPreRideUserStatus]
    )
    const colDefs = useMemo(() => {
        const columns =
            tabName === "trips"
                ? emergencyTrips.map((el) => {
                      if (el.accessorKey === "action") {
                          return {
                              ...el,
                              Cell: actionColumn,
                          }
                      }
                      if (el.accessorKey === "status") {
                          return {
                              ...el,
                              Cell: statusColumn,
                          }
                      }
                      if (el.accessorKey === "payment_type") {
                          return {
                              ...el,
                              Cell: paymentTypeColumn,
                          }
                      }
                      return el
                  })
                : tabName === "users"
                ? userFields?.map((ele) => {
                      if (ele.accessorKey === "prerides") {
                          return {
                              ...ele,
                              Cell: programEditColumn,
                          }
                      }
                      if (ele.accessorKey === "status") {
                          return {
                              ...ele,
                              Cell: usersActionColumn,
                          }
                      }
                      return ele
                  })
                : preRideBudget.map((ele) => {
                      if (
                          ele.id === "created_at" ||
                          ele.accessorKey === "expire_date"
                      )
                          return {
                              ...ele,
                              Filter: CustomeDateFilter,
                          }
                      if (ele.accessorKey === "status")
                          return {
                              ...ele,
                              Cell: preRIDEProgramActionColumn,
                          }
                      return ele
                  })
        return columns
    }, [
        actionColumn,
        paymentTypeColumn,
        preRIDEProgramActionColumn,
        programEditColumn,
        statusColumn,
        tabName,
        usersActionColumn,
    ])

    const { props } = useTableProps({
        columns: colDefs,
        actionButtonProps:
            tabName === "trips"
                ? createEmergencyTrip && {
                      actionName: "Order pre-RIDE Trip",
                      onClick: () => setOpen(true),
                  }
                : tabName === "users"
                ? tId
                    ? ""
                    : teamId
                    ? assignTeamUserPreRideSpending && {
                          actionName: "Add User",
                          onClick: () => setAddUser(true),
                      }
                    : addTripUsers && {
                          actionName: "Add User",
                          onClick: () => setAddUser(true),
                      }
                : (!teamId || preride) &&
                  createPreRideSpending && {
                      actionName: "Add Pre-RIDE Spending",
                      onClick: () => navigate("create"),
                  },
        state: {
            ...state,
            showSkeletons: isLoading || loadingPreRIDE,
            showProgressBars: isFetching || fetchingUsers || fetchingPreRIDE,
            columnVisibility: {
                remaining_balance: tId || teamId,
            },
            columnOrder: undefined,
        },
        exportProps: {
            dataEndPoint:
                tabName === "trips"
                    ? `${teamEndPoint}emergency_trips`
                    : tabName === "users"
                    ? `${teamEndPoint}prerides/users`
                    : `${teamEndPoint}prerides`,
            model: params.filterModel,
            formatter: exporter,
        },
        tabProps: {
            tabs: [
                { value: "program", label: "Pre-RIDE Spending" },
                { value: "users", label: "Users" },
                { value: "trips", label: "Trips" },
            ],
            onChange: handleTabChange,
        },
        refetch:
            tabName === "trips"
                ? refetch
                : tabName === "users"
                ? refetchUsers
                : refetchPreRIDE,
    })

    if (!emergencyTripsPage) return <RestrictedAccess />

    return (
        <Box>
            {open && (
                <EmergencyTripFormDialog
                    open={open}
                    setOpen={setOpen}
                    refetch={refetch}
                />
            )}
            {addUser && (
                <AddMembers
                    open={addUser}
                    setOpen={setAddUser}
                    refetch={refetchUsers}
                />
            )}
            <UpdatePreRIDEPrograms
                user={user}
                open={isEditOpen}
                setOpen={setIsEditOpen}
                refetch={refetchUsers}
            />
            <ViewTripDetails detail={detail} setDetail={setDetail} />

            {/* Approve Dialog */}
            <Dialog
                open={approveDialog.open}
                onClose={() => setApproveDialog({ open: false, trip: null })}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        bgcolor: "#fafafa",
                        mb: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 20,
                            fontWeight: "bold",
                            textAlign: "center",
                        }}
                    >
                        Approve Pre-Ride Trip
                    </Typography>
                    <IconButton
                        onClick={() =>
                            setApproveDialog({ open: false, trip: null })
                        }
                    >
                        <HighlightOff />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ my: "1.5em", px: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: "1rem",
                                mb: 2,
                                color: "text.secondary",
                            }}
                        >
                            You are about to approve this emergency trip
                            request. Please review the details below before
                            confirming.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            bgcolor: "grey.50",
                            p: 2.5,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "grey.200",
                            mb: 3,
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                mb: 2,
                                color: "text.primary",
                            }}
                        >
                            Trip Information
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        minWidth: 100,
                                        color: "text.secondary",
                                    }}
                                >
                                    Passenger:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "text.primary" }}
                                >
                                    {approveDialog.trip?.phone}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        minWidth: 100,
                                        color: "text.secondary",
                                    }}
                                >
                                    From:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "text.primary" }}
                                >
                                    {approveDialog.trip?.start_location}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        minWidth: 100,
                                        color: "text.secondary",
                                    }}
                                >
                                    To:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "text.primary" }}
                                >
                                    {approveDialog.trip?.end_location}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        minWidth: 100,
                                        color: "text.secondary",
                                    }}
                                >
                                    Payment:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "text.primary" }}
                                >
                                    {approveDialog.trip?.payment_type}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            bgcolor: "success.50",
                            p: 2,
                            borderRadius: 1.5,
                            border: "1px solid",
                            borderColor: "success.200",
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ color: "success.main", fontWeight: 500 }}
                        >
                            ✓ Approving this request will allow the passenger to
                            proceed with their emergency trip.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        pr: 3,
                        pt: 2,
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        bgcolor: "#fafafa",
                    }}
                >
                    <Button
                        onClick={() =>
                            setApproveDialog({ open: false, trip: null })
                        }
                        sx={{
                            textTransform: "none",
                            fontWeight: "bold",
                            width: 120,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        sx={{
                            bgcolor: "#72cb85",
                            ":hover": { bgcolor: "#72cb85" },
                            textTransform: "none",
                            fontWeight: "bold",
                            width: 120,
                        }}
                        onClick={() =>
                            updateTripStatus({
                                id: approveDialog.trip?.id,
                                status: "APPROVED",
                            })
                        }
                        disabled={updatingTripStatus}
                    >
                        {updatingTripStatus ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: "white" }}
                            />
                        ) : (
                            "Approve"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog
                open={rejectDialog.open}
                onClose={() => setRejectDialog({ open: false, trip: null })}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        bgcolor: "#fafafa",
                        mb: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 20,
                            fontWeight: "bold",
                            textAlign: "center",
                        }}
                    >
                        Reject Pre-Ride Trip
                    </Typography>
                    <IconButton
                        onClick={() =>
                            setRejectDialog({ open: false, trip: null })
                        }
                    >
                        <HighlightOff />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ my: "1.5em", px: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: "1rem",
                                mb: 2,
                                color: "text.secondary",
                            }}
                        >
                            You are about to reject this emergency trip request.
                            Please review the details below before confirming.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            bgcolor: "grey.50",
                            p: 2.5,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "grey.200",
                            mb: 3,
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                mb: 2,
                                color: "text.primary",
                            }}
                        >
                            Trip Information
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        minWidth: 100,
                                        color: "text.secondary",
                                    }}
                                >
                                    Passenger:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "text.primary" }}
                                >
                                    {rejectDialog.trip?.phone}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        minWidth: 100,
                                        color: "text.secondary",
                                    }}
                                >
                                    From:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "text.primary" }}
                                >
                                    {rejectDialog.trip?.start_location}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        minWidth: 100,
                                        color: "text.secondary",
                                    }}
                                >
                                    To:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "text.primary" }}
                                >
                                    {rejectDialog.trip?.end_location}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        minWidth: 100,
                                        color: "text.secondary",
                                    }}
                                >
                                    Payment:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "text.primary" }}
                                >
                                    {rejectDialog.trip?.payment_type}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            bgcolor: "error.50",
                            p: 2,
                            borderRadius: 1.5,
                            border: "1px solid",
                            borderColor: "error.200",
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ color: "error.main", fontWeight: 500 }}
                        >
                            ⚠ Rejecting this request will deny the passenger`s
                            emergency trip and cannot be undone.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        pr: 3,
                        pt: 2,
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        bgcolor: "#fafafa",
                    }}
                >
                    <Button
                        onClick={() =>
                            setRejectDialog({ open: false, trip: null })
                        }
                        sx={{
                            textTransform: "none",
                            fontWeight: "bold",
                            width: 120,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#d73936",
                            ":hover": { bgcolor: "#d73936" },
                            textTransform: "none",
                            fontWeight: "bold",
                            width: 120,
                        }}
                        onClick={() =>
                            updateTripStatus({
                                id: rejectDialog.trip?.id,
                                status: "REJECTED",
                            })
                        }
                        disabled={updatingTripStatus}
                    >
                        {updatingTripStatus ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: "white" }}
                            />
                        ) : (
                            "Reject"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <Box sx={{ backgroundColor: "white" }}>
                <MaterialReactTable
                    data={
                        tabName === "trips"
                            ? emergencyTripsData?.data?.data ?? []
                            : tabName === "users"
                            ? emergencyTripUsers?.data?.data
                            : preRIDE?.data?.data ?? []
                    }
                    rowCount={
                        tabName === "trips"
                            ? emergencyTripsData?.data?.meta_data?.total ?? 0
                            : tabName === "users"
                            ? emergencyTripUsers?.data?.data?.meta_data?.total
                            : preRIDE?.data?.data?.meta_data?.total ?? 0
                    }
                    {...props}
                    {...onChangeHandlers}
                />
            </Box>
        </Box>
    )
}

export default EmergencyTrips
